import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY } from "@/lib/site-data";
import Logo from "@/components/Logo";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Réalisations", href: "#realisations" },
  { label: "Savoir-faire", href: "#savoir-faire" },
  { label: "Zones", href: "#zones" },
  { label: "FAQ", href: "#faq" },
  { label: "Conseils", href: "/conseils" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-white/5 bg-[#0D0F12]/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        <a href="#accueil" data-testid="nav-logo" className="flex items-center gap-3">
          <Logo size={40} />
          <span className="leading-tight">
            <span className="block font-serif text-base font-semibold tracking-tight text-white">NB</span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">Couverture</span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm text-slate-300 transition-colors duration-300 hover:text-terra"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={COMPANY.phoneHref}
            data-testid="nav-phone-link"
            className="flex items-center gap-2 text-sm font-semibold text-slate-200 transition-colors hover:text-terra"
          >
            <Phone className="h-4 w-4 text-terra" />
            {COMPANY.phone}
          </a>
          <a
            href="#devis"
            data-testid="nav-devis-cta"
            className="rounded-full bg-terra px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terra-hover"
          >
            Devis gratuit
          </a>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center border border-white/10 text-slate-200 lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-b border-white/5 bg-[#0D0F12]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="space-y-1 px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="block py-2.5 text-base text-slate-200"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#devis"
                onClick={() => setOpen(false)}
                data-testid="nav-devis-cta-mobile"
                className="mt-2 block rounded-full bg-terra px-5 py-3 text-center text-sm font-semibold text-white"
              >
                Devis gratuit
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
